import React, { useState } from 'react';
import {
  Box,
  Button,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
} from '@mui/material';
import { useToast } from 'use-toast-mui';
import { createBatchReservation, createPrivateBatch } from '../../../../api/lesson';
import { withOverlapConfirm } from '../../../../utils/overlapConfirm';
import { errorMessages } from '../../../../utils/methods';
import CloseIcon from '@mui/icons-material/Close';
import CustomCheckbox from '../../../CustomCheckbox';
import moment from 'moment';

const FindLessonModal = ({ modal, setModal, bigUser, ticket, getAllTickets }) => {
  const toast = useToast();
  const [selectedItems, setSelectedItems] = useState([]);

  // modal에서 전달받은 데이터
  const batchData = modal.batchReservationData || {};
  const lessons = batchData.items || [];
  const remainingCount = batchData.remainingCount || 0;

  // 개인레슨 일괄생성(새 흐름)인지 — preview API로 받은 후보 세션 목록을 생성하는 경로.
  // 기존 그룹/일일권의 "기존 수업 검색→예약" 경로와 식별자/생성 API가 다르다.
  const isPrivateBatch = batchData.isPrivateBatch === true;
  // 폼에서 입력한 예약 횟수(기존 그룹/일일권 흐름의 선택 상한)
  const reservationCount = batchData.reservationCount || 0;
  // 새 흐름(개인레슨)=사용 횟수가 상한. 기존 흐름은 입력한 예약 횟수가 상한이되, 잔여 이용횟수를 넘지 못한다.
  const selectionMax = isPrivateBatch
    ? batchData.usageMax || 0
    : reservationCount > 0
      ? Math.min(reservationCount, remainingCount)
      : remainingCount;

  // 후보 세션은 아직 미생성이라 courseClassTimeId가 없을 수 있어 인덱스로 식별한다.
  const getKey = (lesson, idx) => (isPrivateBatch ? idx : lesson.courseClassTimeId);

  // 개인레슨 경로만 새 디자인을 적용한다. 그룹레슨은 기존 레이아웃 유지.
  const isPrivateLesson = ticket?.classType === 'PRIVATE_LESSON';

  // 중복 스케줄(이미 예약했거나 시간이 겹치는 수업)은 선택 불가
  const isDuplicated = lesson => lesson?.duplicated === true;

  // 이용 제한 횟수 표시 (없으면 무제한). 백엔드 필드명 변동 대비로 두 표기를 모두 확인한다.
  const weeklyLimit = ticket?.weeklyUsageLimit ?? ticket?.weeklyUsageCount;
  const dailyLimit = ticket?.dailyUsageLimit ?? ticket?.dailyUsageCount;
  const formatLimit = value => (value === null || value === undefined || value === 0 ? '무제한' : `${value}회`);

  const onClose = () => {
    setModal({ ...modal, openFindLesson: false, batchReservationData: null });
    setSelectedItems([]);
  };

  const handleReset = () => {
    // 재설정 — 폼으로 되돌아가되 batchFormData(이전 입력값)는 유지한다.
    setModal({ ...modal, openFindLesson: false, open6: true, batchReservationData: null });
    setSelectedItems([]);
  };

  // 선택 가능한(중복 아닌) 레슨 목록 — 인덱스 키와 함께
  const selectableKeys = lessons.reduce((acc, lesson, idx) => {
    if (!isDuplicated(lesson)) acc.push(getKey(lesson, idx));
    return acc;
  }, []);

  // 체크박스 핸들러
  const handleCheckboxChange = (lesson, idx) => {
    // 중복 스케줄은 선택 불가
    if (isDuplicated(lesson)) {
      return;
    }
    const key = getKey(lesson, idx);
    setSelectedItems(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        // 선택 상한(개인레슨=사용 횟수, 그 외=잔여 이용횟수)을 초과할 수 없음
        if (prev.length >= selectionMax) {
          toast.error(`최대 ${selectionMax}개까지만 선택할 수 있습니다`);
          return prev;
        }
        return [...prev, key];
      }
    });
  };

  // 전체 선택 핸들러 (중복 스케줄 제외)
  const handleSelectAll = () => {
    if (selectedItems.length === selectableKeys.length || selectedItems.length === selectionMax) {
      setSelectedItems([]);
    } else {
      // 선택 상한만큼만 선택
      const maxSelection = Math.min(selectableKeys.length, selectionMax);
      setSelectedItems(selectableKeys.slice(0, maxSelection));
    }
  };

  // 개인레슨 일괄생성 제출 — 선택된 후보 세션만 create API로 생성한다.
  const handleSubmitPrivateBatch = async () => {
    try {
      if (selectedItems.length === 0) {
        toast.error('생성할 레슨을 선택해주세요');
        return;
      }

      // 선택 세션이 잔여 이용횟수를 초과하면 경고 후 중단
      if (selectedItems.length > remainingCount) {
        window.alert(`선택한 레슨 수(${selectedItems.length}개)가 잔여 이용횟수(${remainingCount}회)를 초과합니다.`);
        return;
      }

      const payload = batchData.previewPayload || {};
      const sessions = selectedItems
        .map(idx => lessons[idx])
        .filter(Boolean)
        .map(lesson => ({
          classDate: lesson.classDate,
          dayOfWeek: lesson.dayOfWeek,
          startTime: lesson.startTime,
          endTime: lesson.endTime,
        }));

      // 일일권이면 memberTrialTicketId만, 수강권이면 memberCourseTicketId만 보낸다(둘 다 보내지 않음 → 406 방지)
      const createData = {
        ...(batchData.memberTrialTicketId
          ? { memberTrialTicketId: batchData.memberTrialTicketId }
          : { memberCourseTicketId: batchData.memberCourseTicketId }),
        memberId: payload.memberId,
        trainerId: payload.trainerId,
        exerciseRoomId: payload.exerciseRoomId,
        branchId: payload.branchId,
        sessions,
      };

      await createPrivateBatch(createData, bigUser.token);

      toast.success('레슨이 생성되었습니다');
      if (getAllTickets) {
        getAllTickets();
      }
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 기존 그룹/일일권 일괄예약 제출
  const handleSubmitReservation = async () => {
    try {
      if (selectedItems.length === 0) {
        toast.error('예약할 레슨을 선택해주세요');
        return;
      }

      if (selectedItems.length > remainingCount) {
        toast.error(`잔여 이용횟수(${remainingCount}회)를 초과할 수 없습니다`);
        return;
      }

      // 일괄예약 요청 데이터
      // 일일권이면 memberTrialTicketId만, 수강권이면 memberCourseTicketId만 보낸다(둘 다 보내지 않음).
      const reservationData = {
        memberId: batchData.memberId,
        courseClassTimeIds: selectedItems,
        ...(batchData.memberTrialTicketId
          ? { memberTrialTicketId: batchData.memberTrialTicketId }
          : { memberCourseTicketId: batchData.memberCourseTicketId }),
      };

      await withOverlapConfirm(force => createBatchReservation(reservationData, bigUser.token, force));

      toast.success('레슨 일괄예약이 완료되었습니다');
      if (getAllTickets) {
        getAllTickets();
      }
      onClose();
    } catch (error) {
      // 겹침 확인창에서 취소했으면 안내 없이 모달을 유지한다
      if (!error?.silent) toast.error(errorMessages(error));
    }
  };

  const handleSubmit = isPrivateBatch ? handleSubmitPrivateBatch : handleSubmitReservation;

  // 날짜 포맷 함수
  const formatDateWithDay = dateStr => {
    const date = moment(dateStr);
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    return `${date.format('YYYY.MM.DD')}(${dayNames[date.day()]})`;
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '1000px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  };

  return (
    <Modal
      open={modal.openFindLesson}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="find-lesson-modal-title"
      aria-describedby="find-lesson-modal-description">
      <Box sx={modalStyle}>
        {/* 헤더 영역 */}
        {/* 헤더 영역 */}
        <Typography
          id="lesson-batch-modal-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: '14.5px 24px',
            borderBottom: '1px solid #e1e1e1',
            height: '48px',
            backgroundColor: '#262626',
            borderRadius: '8px 8px 0 0',
          }}>
          <Box sx={{ fontSize: '18px', fontWeight: '600', color: '#ffffff' }}>
            {isPrivateBatch ? '레슨 생성' : '일괄 예약'}
          </Box>
          <Button
            onClick={onClose}
            sx={{
              color: '#ffffff',
              width: '40px',
              height: '40px',
              padding: '0px',
              border: '1px solid #262626',
              backgroundColor: '#262626',
              minWidth: 'unset',
            }}>
            <CloseIcon sx={{ width: '20px', height: '20px' }} />
          </Button>
        </Typography>

        {/* 컨텐츠 영역 */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: '20px 30px',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}>
            {/* 기간 선택 */}
            <Box sx={{ display: 'flex', flexDirection: 'row', mb: '24px' }}>
              <Typography
                className="title"
                sx={{ fontSize: '14px', fontWeight: 'bold', mr: '25px', minWidth: '100px', color: '#3a3a3a' }}>
                일괄예약 기간
              </Typography>
              <Typography
                className="title"
                sx={{ fontSize: '14px', fontWeight: 'bold', mr: '25px', color: '#3a3a3a' }}>
                {isPrivateBatch
                  ? batchData.previewPayload?.registrationStartDate && batchData.previewPayload?.registrationEndDate
                    ? `${formatDateWithDay(batchData.previewPayload.registrationStartDate)} ~ ${formatDateWithDay(batchData.previewPayload.registrationEndDate)}`
                    : '-'
                  : batchData.startDate && batchData.endDate
                    ? `${formatDateWithDay(batchData.startDate)} ~ ${formatDateWithDay(batchData.endDate)} ${batchData.startTime || ''} ${batchData.endTime || ''}`
                    : '-'}
              </Typography>
            </Box>

            {/* 선택 횟수 — 개인레슨은 사용 횟수(상한) 대비, 그 외는 잔여 이용횟수 대비 */}
            <Box sx={{ display: 'flex', flexDirection: 'row', mb: '24px' }}>
              <Typography
                className="title"
                sx={{ fontSize: '14px', fontWeight: 'bold', mr: '25px', minWidth: '100px', color: '#3a3a3a' }}>
                {isPrivateBatch ? '사용 횟수' : '예약 횟수'}
              </Typography>
              <Typography
                className="title"
                sx={{ fontSize: '14px', fontWeight: 'bold', mr: '25px', color: '#3a3a3a' }}>
                {selectedItems.length}/{selectionMax}회
              </Typography>
            </Box>

            {/* 이용 제한 횟수 (개인레슨) */}
            {isPrivateLesson && (
              <Box sx={{ display: 'flex', flexDirection: 'row', mb: '24px' }}>
                <Typography
                  className="title"
                  sx={{ fontSize: '14px', fontWeight: 'bold', mr: '25px', minWidth: '100px', color: '#3a3a3a' }}>
                  이용 제한 횟수
                </Typography>
                <Typography
                  className="title"
                  sx={{ fontSize: '14px', fontWeight: 'bold', mr: '25px', color: '#3a3a3a' }}>
                  주간 이용 제한 : {formatLimit(weeklyLimit)} / 일일 이용 제한 : {formatLimit(dailyLimit)}
                </Typography>
              </Box>
            )}

            {/* 검색 결과 테이블 */}
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '400px', overflowY: 'auto' }}>
              <Table
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& .MuiTableCell-root': {
                    border: '1px solid #e0e0e0',
                  },
                }}
                stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: '600', backgroundColor: '#f5f5f5', width: '50px' }}>
                      <CustomCheckbox
                        checked={selectableKeys.length > 0 && selectedItems.length === selectableKeys.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                      센터
                    </TableCell>
                    {!isPrivateLesson && (
                      <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                        클래스명
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                      레슨일시
                    </TableCell>
                    {isPrivateLesson && (
                      <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                        사용횟수
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                      담당강사
                    </TableCell>
                    {!isPrivateLesson && (
                      <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                        수업정원
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lessons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isPrivateLesson ? 5 : 6} align="center" sx={{ py: 4, color: '#999' }}>
                        조회된 레슨이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    lessons.map((lesson, idx) => {
                      const duplicated = isDuplicated(lesson);
                      const rowKey = getKey(lesson, idx);
                      return (
                        <TableRow
                          key={isPrivateBatch ? `${lesson.classDate}-${lesson.startTime}-${idx}` : rowKey}
                          sx={{
                            '&:hover': {
                              backgroundColor: duplicated ? 'transparent' : '#f9f9f9',
                              cursor: duplicated ? 'not-allowed' : 'pointer',
                            },
                          }}
                          onClick={() => handleCheckboxChange(lesson, idx)}>
                          <TableCell align="center" sx={{ width: '50px' }}>
                            <CustomCheckbox
                              checked={selectedItems.includes(rowKey)}
                              disabled={duplicated}
                              onChange={() => handleCheckboxChange(lesson, idx)}
                            />
                          </TableCell>
                          <TableCell align="center">{lesson.branchName || '-'}</TableCell>
                          {!isPrivateLesson && <TableCell align="center">{lesson.className}</TableCell>}
                          <TableCell align="center">
                            {formatDateWithDay(lesson.classDate)} {lesson.startTime}-{lesson.endTime}
                            {duplicated && (
                              <Typography
                                component="span"
                                sx={{ color: '#e53935', fontSize: '13px', fontWeight: '600', ml: '6px' }}>
                                (중복 스케줄)
                              </Typography>
                            )}
                          </TableCell>
                          {isPrivateLesson && <TableCell align="center">1 회</TableCell>}
                          <TableCell align="center">{lesson.trainerName}</TableCell>
                          {!isPrivateLesson && (
                            <TableCell align="center">
                              {lesson.currentMemberCount} / {lesson.maxMemberCount}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* 버튼 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: '10px',
                p: '20px 30px',
              }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderColor: '#666',
                  backgroundColor: '#666',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  minWidth: '100px',
                  '&:hover': {
                    borderColor: '#666',
                    backgroundColor: '#666',
                    color: '#fff',
                  },
                }}>
                취소
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{
                  borderColor: '#ddd',
                  color: '#666',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  minWidth: '100px',
                  '&:hover': {
                    borderColor: '#ddd',
                    backgroundColor: '#f5f5f5',
                  },
                }}>
                재설정
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  backgroundColor: '#0F7F85',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  minWidth: '100px',
                  '&:hover': {
                    backgroundColor: '#0d6b70',
                  },
                }}>
                {isPrivateBatch ? '일괄 생성' : '레슨예약'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default FindLessonModal;
